
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { ArrowLeft, ExternalLink, Star, Users, Zap, Settings } from 'lucide-react';

const OutilsPage = () => {
  const { toolName } = useParams();

  const toolsData: Record<string, any> = {
    'adobe-creative-suite': {
      name: 'Adobe Creative Suite',
      category: 'Design & Création',
      description: 'Suite complète d\'outils de création graphique et multimédia professionnels.',
      longDescription: 'Adobe Creative Suite est la référence mondiale pour la création graphique, vidéo et web. Elle comprend Photoshop, Illustrator, InDesign, Premiere Pro, After Effects et bien d\'autres outils essentiels pour les professionnels créatifs.',
      useCases: [
        'Création d\'identités visuelles complètes',
        'Retouche photo professionnelle',
        'Conception de supports print et digital',
        'Montage vidéo et motion design',
        'Création de logos et illustrations vectorielles'
      ],
      advantages: [
        'Standard de l\'industrie créative',
        'Intégration parfaite entre les logiciels',
        'Mises à jour régulières et nouvelles fonctionnalités',
        'Communauté mondiale et ressources abondantes'
      ],
      pricing: 'Abonnement mensuel - Solution professionnelle',
      difficulty: 'Avancé',
      rating: 5
    },
    'canva-pro': {
      name: 'Canva Pro',
      category: 'Design Simplifié',
      description: 'Outil de design accessible pour créer rapidement des visuels professionnels.',
      longDescription: 'Canva Pro démocratise le design en offrant des templates et outils intuitifs pour créer des visuels impactants sans expertise technique approfondie.',
      useCases: [
        'Posts réseaux sociaux',
        'Présentations business',
        'Flyers et affiches',
        'Stories Instagram',
        'Documents marketing'
      ],
      advantages: [
        'Interface ultra-intuitive',
        'Bibliothèque de templates immense',
        'Collaboration en temps réel',
        'Export dans tous les formats'
      ],
      pricing: 'Freemium avec version Pro',
      difficulty: 'Débutant',
      rating: 4
    },
    'wordpress': {
      name: 'WordPress',
      category: 'CMS & Développement Web',
      description: 'Système de gestion de contenu le plus populaire au monde.',
      longDescription: 'WordPress propulse plus de 40% des sites web mondiaux. Flexible et extensible, il permet de créer tout type de site web, du blog personnel au site e-commerce complexe.',
      useCases: [
        'Sites vitrine entreprise',
        'Blogs professionnels',
        'E-commerce avec WooCommerce',
        'Sites portfolio',
        'Plateformes de formation'
      ],
      advantages: [
        'Open source et gratuit',
        'Écosystème de plugins immense',
        'SEO-friendly par défaut',
        'Communauté mondiale active'
      ],
      pricing: 'Gratuit + coûts d\'hébergement',
      difficulty: 'Intermédiaire',
      rating: 5
    },
    'drupal': {
      name: 'Drupal',
      category: 'CMS Avancé',
      description: 'CMS open-source robuste pour sites web complexes et applications.',
      longDescription: 'Drupal est un système de gestion de contenu puissant, idéal pour les sites web d\'entreprise complexes nécessitant des fonctionnalités avancées et une sécurité renforcée.',
      useCases: [
        'Sites gouvernementaux',
        'Portails d\'entreprise',
        'Sites multilingues complexes',
        'Intranets d\'entreprise',
        'Plateformes communautaires'
      ],
      advantages: [
        'Sécurité renforcée',
        'Flexibilité architecturale',
        'Gestion multi-sites',
        'API robuste'
      ],
      pricing: 'Gratuit + développement personnalisé',
      difficulty: 'Avancé',
      rating: 4
    },
    'hubspot': {
      name: 'HubSpot',
      category: 'CRM & Marketing Automation',
      description: 'Plateforme tout-en-un pour le marketing, les ventes et le service client.',
      longDescription: 'HubSpot centralise toutes les activités marketing et commerciales dans une plateforme intégrée, de la génération de leads à la fidélisation client.',
      useCases: [
        'Gestion relation client (CRM)',
        'Email marketing automatisé',
        'Landing pages optimisées',
        'Scoring et nurturing leads',
        'Analyse performance commerciale'
      ],
      advantages: [
        'Interface intuitive',
        'Intégration native complète',
        'Formation et support excellents',
        'Scalabilité pour toutes tailles d\'entreprise'
      ],
      pricing: 'Freemium avec plans payants',
      difficulty: 'Intermédiaire',
      rating: 5
    },
    'google-analytics': {
      name: 'Google Analytics',
      category: 'Analyse & Data',
      description: 'Outil d\'analyse web pour mesurer et optimiser les performances digitales.',
      longDescription: 'Google Analytics est l\'outil de référence pour comprendre le comportement des visiteurs et mesurer l\'efficacité des actions marketing digitales.',
      useCases: [
        'Analyse du trafic web',
        'Suivi des conversions',
        'Optimisation SEO',
        'Mesure ROI campagnes',
        'Analyse comportementale'
      ],
      advantages: [
        'Gratuit et complet',
        'Intégration Google Ads',
        'Rapports personnalisables',
        'Données en temps réel'
      ],
      pricing: 'Gratuit',
      difficulty: 'Intermédiaire',
      rating: 5
    },
    'google-ads': {
      name: 'Google Ads',
      category: 'Publicité Digitale',
      description: 'Plateforme publicitaire de Google pour créer et gérer des campagnes.',
      longDescription: 'Google Ads permet de créer des campagnes publicitaires ciblées sur le réseau de recherche Google et ses partenaires pour générer du trafic qualifié.',
      useCases: [
        'Campagnes de recherche Google',
        'Display et remarketing',
        'Publicités YouTube',
        'Shopping ads e-commerce',
        'Campagnes locales'
      ],
      advantages: [
        'Ciblage précis',
        'Contrôle du budget',
        'Mesure ROI en temps réel',
        'Audience mondiale'
      ],
      pricing: 'Paiement au clic ou impression',
      difficulty: 'Intermédiaire',
      rating: 4
    },
    'meta-business': {
      name: 'Meta Business',
      category: 'Réseaux Sociaux & Publicité',
      description: 'Suite d\'outils pour gérer la présence sur Facebook et Instagram.',
      longDescription: 'Meta Business Suite centralise la gestion des pages Facebook et Instagram, permettant de programmer du contenu et créer des campagnes publicitaires ciblées.',
      useCases: [
        'Gestion pages Facebook/Instagram',
        'Campagnes publicitaires sociales',
        'Programmation de contenu',
        'Analyse performance sociale',
        'Remarketing audiences'
      ],
      advantages: [
        'Interface unifiée Facebook/Instagram',
        'Ciblage démographique précis',
        'Formats publicitaires variés',
        'Analytics détaillés'
      ],
      pricing: 'Gratuit + budget publicitaire',
      difficulty: 'Intermédiaire',
      rating: 4
    },
    'linkedin-ads': {
      name: 'LinkedIn Ads',
      category: 'Marketing B2B',
      description: 'Plateforme publicitaire professionnelle pour le marketing B2B.',
      longDescription: 'LinkedIn Ads permet de cibler précisément les professionnels selon leur secteur, poste et entreprise pour des campagnes B2B efficaces.',
      useCases: [
        'Génération de leads B2B',
        'Recrutement et employer branding',
        'Promotion d\'événements professionnels',
        'Content marketing B2B',
        'Campagnes de notoriété sectorielles'
      ],
      advantages: [
        'Ciblage professionnel unique',
        'Audience qualifiée B2B',
        'Formats adaptés au contenu pro',
        'ROI mesurable'
      ],
      pricing: 'Système d\'enchères',
      difficulty: 'Intermédiaire',
      rating: 4
    },
    'hootsuite': {
      name: 'Hootsuite',
      category: 'Social Media Management',
      description: 'Plateforme de gestion et programmation pour réseaux sociaux.',
      longDescription: 'Hootsuite permet de gérer plusieurs comptes de réseaux sociaux depuis une interface unique, avec programmation, monitoring et analytics.',
      useCases: [
        'Programmation multi-plateformes',
        'Monitoring de mentions',
        'Gestion d\'équipe sociale',
        'Reporting cross-platform',
        'Veille concurrentielle'
      ],
      advantages: [
        'Support de nombreux réseaux',
        'Interface de gestion centralisée',
        'Outils de collaboration',
        'Analytics consolidés'
      ],
      pricing: 'Plans mensuels par utilisateur',
      difficulty: 'Intermédiaire',
      rating: 4
    },
    'buffer': {
      name: 'Buffer',
      category: 'Social Media Scheduling',
      description: 'Outil simple de programmation et analyse pour réseaux sociaux.',
      longDescription: 'Buffer se concentre sur la simplicité pour programmer du contenu sur les réseaux sociaux et analyser les performances avec des rapports clairs.',
      useCases: [
        'Programmation de posts',
        'Analyse de performance',
        'Gestion multi-comptes',
        'Collaboration équipe',
        'Optimisation horaires de publication'
      ],
      advantages: [
        'Interface épurée et intuitive',
        'Analytics simples et clairs',
        'Prix abordable',
        'Excellent support client'
      ],
      pricing: 'Freemium avec plans payants',
      difficulty: 'Débutant',
      rating: 4
    },
    'mailchimp': {
      name: 'Mailchimp',
      category: 'Email Marketing',
      description: 'Plateforme leader pour l\'email marketing et l\'automation.',
      longDescription: 'Mailchimp propose des outils complets pour créer, envoyer et analyser des campagnes email avec des fonctionnalités d\'automation avancées.',
      useCases: [
        'Newsletters et campagnes email',
        'Automation marketing',
        'Segmentation d\'audience',
        'A/B testing emailing',
        'Intégration e-commerce'
      ],
      advantages: [
        'Templates email attractifs',
        'Automation visuelle intuitive',
        'Analytics détaillés',
        'Intégrations nombreuses'
      ],
      pricing: 'Freemium jusqu\'à 2000 contacts',
      difficulty: 'Débutant',
      rating: 5
    },
    'sendinblue': {
      name: 'Sendinblue',
      category: 'Email & SMS Marketing',
      description: 'Plateforme européenne pour email marketing, SMS et chat.',
      longDescription: 'Sendinblue (maintenant Brevo) offre une solution complète de marketing digital incluant email, SMS, et outils CRM avec un excellent rapport qualité-prix.',
      useCases: [
        'Campagnes email et SMS',
        'Marketing automation',
        'CRM intégré',
        'Landing pages',
        'Chat en direct'
      ],
      advantages: [
        'Solution européenne RGPD-compliant',
        'Tarifs compétitifs',
        'Support multicanal',
        'Interface en français'
      ],
      pricing: 'Freemium avec volume généreux',
      difficulty: 'Débutant',
      rating: 4
    },
    'figma': {
      name: 'Figma',
      category: 'Design UI/UX',
      description: 'Outil de design collaboratif pour interfaces et prototypage.',
      longDescription: 'Figma révolutionne le design d\'interface en permettant la collaboration en temps réel et le prototypage interactif directement dans le navigateur.',
      useCases: [
        'Design d\'interfaces web/mobile',
        'Prototypage interactif',
        'Design systems',
        'Collaboration designer-développeur',
        'Wireframing et mockups'
      ],
      advantages: [
        'Collaboration temps réel',
        'Basé navigateur, multiplateforme',
        'Prototypage avancé',
        'Handoff développeur facilité'
      ],
      pricing: 'Gratuit pour usage personnel',
      difficulty: 'Intermédiaire',
      rating: 5
    },
    'sketch': {
      name: 'Sketch',
      category: 'Design Vectoriel',
      description: 'Outil de design vectoriel spécialisé pour les interfaces.',
      longDescription: 'Sketch est l\'outil de référence sur Mac pour le design d\'interfaces, offrant des fonctionnalités avancées pour créer des designs pixel-perfect.',
      useCases: [
        'Design d\'interfaces iOS/macOS',
        'Création d\'icônes vectorielles',
        'Design systems avancés',
        'Export assets développeurs',
        'Design responsive'
      ],
      advantages: [
        'Optimisé pour les interfaces',
        'Écosystème de plugins riche',
        'Symbols et librairies',
        'Performance excellente'
      ],
      pricing: 'Licence annuelle Mac uniquement',
      difficulty: 'Intermédiaire',
      rating: 4
    },
    'invision': {
      name: 'InVision',
      category: 'Prototypage & Collaboration',
      description: 'Plateforme de prototypage et collaboration design.',
      longDescription: 'InVision permet de transformer des designs statiques en prototypes interactifs et facilite la collaboration entre équipes design et développement.',
      useCases: [
        'Prototypage interactif',
        'Feedback et annotations',
        'Workflow design-dev',
        'Présentation clients',
        'Tests utilisateurs'
      ],
      advantages: [
        'Prototypage sans code',
        'Outils de feedback intégrés',
        'Intégrations design tools',
        'Partage sécurisé'
      ],
      pricing: 'Freemium avec limitations',
      difficulty: 'Débutant',
      rating: 3
    },
    'slack': {
      name: 'Slack',
      category: 'Communication & Collaboration',
      description: 'Plateforme de communication d\'équipe et collaboration.',
      longDescription: 'Slack centralise la communication d\'équipe avec des canaux organisés, intégrations d\'outils et fonctionnalités de collaboration avancées.',
      useCases: [
        'Communication d\'équipe',
        'Gestion de projets',
        'Intégrations outils métier',
        'Partage de fichiers',
        'Réunions et appels'
      ],
      advantages: [
        'Organisation en canaux',
        'Intégrations multiples',
        'Recherche puissante',
        'Apps et workflows'
      ],
      pricing: 'Freemium avec plans équipe',
      difficulty: 'Débutant',
      rating: 5
    },
    'asana': {
      name: 'Asana',
      category: 'Gestion de Projet',
      description: 'Outil de gestion de projet et collaboration d\'équipe.',
      longDescription: 'Asana aide les équipes à organiser, suivre et gérer leurs projets avec des vues multiples et des fonctionnalités de collaboration avancées.',
      useCases: [
        'Planification de projets',
        'Suivi des tâches',
        'Gestion d\'équipe',
        'Reporting de progression',
        'Workflows automatisés'
      ],
      advantages: [
        'Interface intuitive',
        'Vues multiples (liste, board, timeline)',
        'Automation des tâches',
        'Intégrations nombreuses'
      ],
      pricing: 'Gratuit jusqu\'à 15 membres',
      difficulty: 'Débutant',
      rating: 4
    },
    'trello': {
      name: 'Trello',
      category: 'Organisation Kanban',
      description: 'Outil d\'organisation visuelle basé sur la méthode Kanban.',
      longDescription: 'Trello utilise un système de tableaux, listes et cartes inspiré de Kanban pour organiser visuellement les projets et tâches.',
      useCases: [
        'Gestion de tâches personnelles',
        'Projets créatifs',
        'Suivi de pipeline',
        'Organisation d\'événements',
        'Brainstorming visuel'
      ],
      advantages: [
        'Simplicité d\'utilisation',
        'Approche visuelle Kanban',
        'Power-ups extensibles',
        'Collaboration facile'
      ],
      pricing: 'Freemium avec options payantes',
      difficulty: 'Débutant',
      rating: 4
    },
    'monday-com': {
      name: 'Monday.com',
      category: 'Work Management',
      description: 'Plateforme de gestion du travail visuelle et collaborative.',
      longDescription: 'Monday.com offre une approche colorée et visuelle de la gestion de projet avec des tableaux personnalisables et des automations puissantes.',
      useCases: [
        'Gestion de projets complexes',
        'Suivi de campagnes marketing',
        'Gestion de pipeline ventes',
        'Planning d\'équipe',
        'Reporting exécutif'
      ],
      advantages: [
        'Interface visuelle colorée',
        'Personnalisation poussée',
        'Automations avancées',
        'Intégrations nombreuses'
      ],
      pricing: 'Plans payants par utilisateur',
      difficulty: 'Intermédiaire',
      rating: 4
    }
  };

  const currentTool = toolsData[toolName || ''] || {
    name: 'Outil non trouvé',
    category: 'Inconnu',
    description: 'Cet outil n\'est pas encore documenté.',
    longDescription: 'Nous travaillons à documenter tous nos outils. Revenez bientôt !',
    useCases: [],
    advantages: [],
    pricing: 'N/A',
    difficulty: 'N/A',
    rating: 0
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Débutant': return 'text-green-600 bg-green-50';
      case 'Intermédiaire': return 'text-yellow-600 bg-yellow-50';
      case 'Avancé': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section className="pt-32 pb-20">
        <div className="container mx-auto px-6">
          <Link 
            to="/expertise" 
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-8 transition-colors"
          >
            <ArrowLeft size={20} />
            Retour à l'expertise
          </Link>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white border border-navy-100 rounded-2xl p-8 shadow-lg mb-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <span className="inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-medium mb-4">
                    {currentTool.category}
                  </span>
                  <h1 className="text-4xl font-light text-navy-900 mb-4">{currentTool.name}</h1>
                  <p className="text-xl text-navy-600 font-light">{currentTool.description}</p>
                </div>
                {currentTool.rating > 0 && (
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        size={20} 
                        className={i < currentTool.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'} 
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="flex items-center gap-3">
                  <Settings className="text-blue-600" size={20} />
                  <div>
                    <span className="text-sm text-navy-500">Niveau de difficulté</span>
                    <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(currentTool.difficulty)}`}>
                      {currentTool.difficulty}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Zap className="text-blue-600" size={20} />
                  <div>
                    <span className="text-sm text-navy-500">Tarification</span>
                    <p className="font-medium text-navy-900">{currentTool.pricing}</p>
                  </div>
                </div>
              </div>

              <p className="text-navy-600 leading-relaxed">{currentTool.longDescription}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white border border-navy-100 rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-navy-900 mb-4 flex items-center gap-2">
                  <Users className="text-blue-600" size={20} />
                  Cas d'usage
                </h3>
                <ul className="space-y-3">
                  {currentTool.useCases.map((useCase: string, index: number) => (
                    <li key={index} className="flex items-start gap-2 text-navy-600">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      {useCase}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white border border-navy-100 rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-navy-900 mb-4 flex items-center gap-2">
                  <Zap className="text-blue-600" size={20} />
                  Avantages clés
                </h3>
                <ul className="space-y-3">
                  {currentTool.advantages.map((advantage: string, index: number) => (
                    <li key={index} className="flex items-start gap-2 text-navy-600">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                      {advantage}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-8 text-center">
              <div className="bg-gradient-to-r from-blue-50 to-navy-50 rounded-2xl p-8">
                <h3 className="text-2xl font-semibold text-navy-900 mb-4">
                  Besoin d'aide avec {currentTool.name} ?
                </h3>
                <p className="text-navy-600 mb-6">
                  Je maîtrise parfaitement cet outil et peux vous accompagner dans vos projets.
                </p>
                <Link 
                  to="/#contact"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-navy-700 text-white px-8 py-3 rounded-full font-medium hover:shadow-lg transition-all duration-300"
                >
                  Discutons de votre projet
                  <ExternalLink size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default OutilsPage;
